/**
 * TAHLEEL.ai Login Page — matching tahleel.ai landing page theme
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock } from 'lucide-react';
import { supabase } from '../services/api';

const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) navigate('/dashboard');
    });
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      if (!email || !password) throw new Error('Email and password are required');
      if (!email.includes('@')) throw new Error('Please enter a valid email address');
      if (password.length < 6) throw new Error('Password must be at least 6 characters');
      const { data, error: authError } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (authError) throw authError;
      if (!data.session) throw new Error('Login failed. Please try again.');
      setSuccess(t('auth.loggingIn'));
      setTimeout(() => navigate('/dashboard'), 1000);
    } catch (err) {
      let msg = 'Login failed. Please try again.';
      if (err.message.includes('Invalid login credentials')) msg = 'Invalid email or password.';
      else if (err.message.includes('Email not confirmed')) msg = 'Please verify your email address.';
      else if (err.message) msg = err.message;
      setError(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex" style={{ background: '#f8fafc' }}>

      {/* LEFT — Navy brand panel (hidden mobile) */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center px-12 relative overflow-hidden" style={{ background: '#0a1628' }}>
        {/* Grid overlay */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.03) 1px,transparent 1px)',
          backgroundSize: '60px 60px'
        }} />

        <div className="relative z-10 max-w-md">
          <img src="/logo.svg" alt="" className="w-16 h-16 mb-8" />
          <h1 className="text-4xl font-extrabold text-white mb-3 tracking-tight">
            TAHLEEL<span style={{ color: '#f59e0b' }}>.ai</span>
          </h1>
          <p className="text-lg font-medium mb-2" style={{ color: 'rgba(255,255,255,0.7)' }}>
            AI-Powered Sports Tactics Platform
          </p>
          <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Upload match video and get a full tactical report in 5 minutes.
            Formation detection. Weakness mapping. Counter-tactics. Training plans.
          </p>

          <div className="mt-10 flex gap-6" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '13px' }}>
            <span>&#9917; 5-min analysis</span>
            <span>&#128202; 80%+ accuracy</span>
            <span>&#127942; Pro-grade reports</span>
          </div>
        </div>
      </div>

      {/* RIGHT — Login form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">

          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img src="/logo.svg" alt="" className="w-14 h-14 mx-auto mb-4" />
            <h1 className="text-2xl font-extrabold" style={{ color: '#0f172a' }}>
              TAHLEEL<span style={{ color: '#f59e0b' }}>.ai</span>
            </h1>
          </div>

          <h2 className="text-2xl font-bold mb-1" style={{ color: '#0f172a' }}>{t('auth.welcomeBack')}</h2>
          <p className="text-sm mb-6" style={{ color: '#64748b' }}>{t('auth.signIn')}</p>

          {/* Language */}
          <div className="flex gap-2 mb-6">
            {['en', 'ar'].map(lang => (
              <button key={lang} onClick={() => i18n.changeLanguage(lang)}
                className="px-3 py-1.5 rounded-md text-xs font-semibold transition-all"
                style={{
                  background: i18n.language === lang ? '#f59e0b' : '#f8fafc',
                  color: i18n.language === lang ? '#0a1628' : '#64748b',
                  border: `1px solid ${i18n.language === lang ? '#f59e0b' : '#e2e8f0'}`
                }}>
                {lang === 'en' ? 'English' : '\u0627\u0644\u0639\u0631\u0628\u064A\u0629'}
              </button>
            ))}
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg text-sm font-medium" style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>{error}</div>
          )}
          {success && (
            <div className="mb-4 p-3 rounded-lg text-sm font-medium" style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}>{success}</div>
          )}

          <form id="login-form" onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0f172a' }}>{t('auth.email')}</label>
              <div className="relative">
                <Mail className="absolute left-3 rtl:right-3 rtl:left-auto top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#94a3b8' }} />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} disabled={loading}
                  placeholder={t('auth.enterEmail')} required
                  className="w-full pl-10 rtl:pr-10 rtl:pl-3 pr-3 py-3 rounded-lg text-sm font-medium"
                  style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#0f172a', outline: 'none' }}
                  onFocus={e => { e.target.style.borderColor = '#f59e0b'; e.target.style.boxShadow = '0 0 0 2px rgba(245,158,11,0.12)'; e.target.style.background = '#fff'; }}
                  onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; e.target.style.background = '#f8fafc'; }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0f172a' }}>{t('auth.password')}</label>
              <div className="relative">
                <Lock className="absolute left-3 rtl:right-3 rtl:left-auto top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#94a3b8' }} />
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} disabled={loading}
                  placeholder={t('auth.enterPassword')} required minLength={6}
                  className="w-full pl-10 rtl:pr-10 rtl:pl-3 pr-3 py-3 rounded-lg text-sm font-medium"
                  style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#0f172a', outline: 'none' }}
                  onFocus={e => { e.target.style.borderColor = '#f59e0b'; e.target.style.boxShadow = '0 0 0 2px rgba(245,158,11,0.12)'; e.target.style.background = '#fff'; }}
                  onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; e.target.style.background = '#f8fafc'; }}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-3.5 h-3.5 rounded" style={{ accentColor: '#f59e0b' }} />
                <span className="text-xs" style={{ color: '#64748b' }}>Remember me</span>
              </label>
              <Link to="/forgot-password" className="text-xs font-semibold" style={{ color: '#f59e0b' }}>{t('auth.forgotPassword')}</Link>
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-3 rounded-lg font-bold text-sm transition-all"
              style={{
                background: loading ? '#e2e8f0' : '#f59e0b',
                color: loading ? '#94a3b8' : '#0a1628',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}>
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                  {t('auth.loggingIn')}
                </span>
              ) : t('auth.signIn')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <span className="text-sm" style={{ color: '#64748b' }}>{t('auth.noAccount')} </span>
            <Link to="/register" className="text-sm font-semibold" style={{ color: '#f59e0b' }}>{t('auth.signUp')} &rarr;</Link>
          </div>

          <div className="mt-6 pt-4 text-center" style={{ borderTop: '1px solid #e2e8f0' }}>
            <p className="text-xs" style={{ color: '#94a3b8' }}>
              Need help? <a href="mailto:support@tahleel.ai" className="font-semibold" style={{ color: '#f59e0b' }}>support@tahleel.ai</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
